// src/components/prayers/CreatePrayerModal.tsx
import React, { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import PrayerForm from '@/components/forms/PrayerForm';
import { StandardDialogTemplate } from '@/components/shared/StandardDialogTemplate';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { LAYERS } from '@/utils/layers';

interface CreatePrayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrayerCreated?: () => void;
}

interface PrayerFormData {
  title: string | null;
  content: string;
  category: string | null;
  visibility: 'public' | 'private';
}

export default function CreatePrayerModal({
  isOpen,
  onClose,
  onPrayerCreated,
}: CreatePrayerModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpanded = () => {
    console.log('Toggle expanded clicked, current state:', isExpanded);
    setIsExpanded((prev) => !prev);
  };

  const handleSubmit = async (data: PrayerFormData) => {
    setIsSubmitting(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast.error('Please sign in to share your prayer.');
        return;
      }

      const { data: insertData, error } = await supabase
        .from('prayers')
        .insert({
          title: data.title || null,
          content: data.content,
          category: data.category || null,
          visibility: data.visibility || 'public',
          is_public: data.visibility === 'public',
          author_id: user.id,
          author_name:
            user.user_metadata?.full_name ||
            user.email?.split('@')[0] ||
            'Anonymous',
        })
        .select(); // ✅ return inserted row for debugging

      if (error) {
        console.error('❌ Supabase insert error:', error);
        toast.error('Error Sharing Prayer', {
          description: error.message || 'Insert failed',
        });
        return;
      }

      console.log('✅ Prayer insert success:', insertData);
      toast.success('Prayer Shared Successfully', {
        description: 'Your prayer has been shared with the community.',
      });

      onClose();
      onPrayerCreated?.();
    } catch (error: any) {
      console.error(
        '⚠️ Error creating prayer:',
        error?.message || JSON.stringify(error)
      );
      toast.error('Error Sharing Prayer', {
        description:
          error?.message ||
          'There was an error sharing your prayer. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom footer with form actions
  const customFooter = (
    <div className="mx-4 mb-4">
      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1 border-border hover:bg-accent text-foreground text-sm"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="prayer-form"
          className="flex-1 app-button-cta font-medium text-sm"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sharing Prayer...
            </>
          ) : (
            'Share Prayer'
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <StandardDialogTemplate
      isOpen={isOpen}
      onClose={onClose}
      isExpanded={isExpanded}
      onToggleExpanded={handleToggleExpanded}
      gesturePreset="close-only"
      gestureCallbacks={{
        onClose: onClose,
      }}
      audioConfig={{ enabled: false }}
      backdropConfig={{
        enabled: true,
        scaleTransform: 1.0,
        oppositeTheme: true,
        onClick: onClose,
      }}
      headerConfig={{
        showLogo: false,
        showAvatar: false,
        variant: 'primary',
        onClose: onClose, // Ensure close button is visible
      }}
      footerConfig={{ showCTA: false }}
      ariaLabel="Share Prayer Form"
      className={isExpanded ? 'h-[95vh] max-h-[95vh]' : 'h-[85vh] max-h-[85vh]'}
      customZIndex={LAYERS.GENERATED_PRAYER}
    >
      <div className="flex flex-col h-full min-h-[400px]">
        <div className="flex-1 overflow-y-auto">
          <PrayerForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={onClose}
            showActions={false} // Hide inline actions, we'll use footer
          />
        </div>
        {customFooter}
      </div>
    </StandardDialogTemplate>
  );
}
